/**
 * PlaylistSharingDropdown — Per-playlist visibility chooser rendered inside
 * the My Playlists tab.
 *
 * i18n: owns its own `playlistSharingDropdown` slice registered in
 * `services/static-text/language-*.js`.  Because this component is rendered
 * as the `statusTag` slot of `<CommunityPlaylistRowHeader>` (which sets up
 * its own `<StaticTextProvider staticText={playlistRowHeaderSlice}>` and
 * would otherwise be the nearest provider for our `useTranslatedText` calls),
 * we follow the Outer/Inner pattern used by `PlaylistShareButton` — the HOC
 * injects our slice and the Outer re-establishes a provider so the inner
 * hook resolves against the right Map.  Keys are namespaced under `sharing`
 * following the OracleCommons convention (`useTranslatedText({ keys: ['ns',
 * 'subkey'] })`).
 */

import React, { useMemo } from 'react'
import { compose } from 'recompose'
import Button, { TYPES as BUTTON_TYPES } from '../../../../../../Button/index'
import useDisclosure from '../../../../../../../services/community/shared/useDisclosure'
import Check from '../../../../../../Icon/icons/Check/index'
import EyeShowSolid from '../../../../../../Icon/icons/EyeShowSolid/index'
import EyeHideSolid from '../../../../../../Icon/icons/EyeHideSolid/index'
import ChevronDown from '../../../../../../Icon/icons/ChevronDown/index'
import ChevronUp from '../../../../../../Icon/icons/ChevronUp/index'
import CommunitySplitPillTag from '../../../../../shared/CommunitySplitPillTag/index'
import { connect as connectStaticText } from '../../../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../../../StaticText/StaticTextContext'
import type {
  PlaylistVisibilityLevel,
} from '../../../../../../../services/community/types'

export const VISIBILITY = {
  PUBLIC: 'PUBLIC',
  ANONYMOUS: 'ANONYMOUS',
  PRIVATE: 'PRIVATE',
} as const satisfies Record<string, PlaylistVisibilityLevel>

export const isPlaylistVisibilityLevel = (
  value: string | null | undefined,
): value is PlaylistVisibilityLevel => (
  value === VISIBILITY.PUBLIC
  || value === VISIBILITY.ANONYMOUS
  || value === VISIBILITY.PRIVATE
)

export interface PlaylistSharingDropdownProps {
  visibility: string
  onChangeVisibility: (vis: PlaylistVisibilityLevel) => void
  allowedVisibilities?: string[]
  followerCount?: number
  /**
   * 'playlist' (default) labels PUBLIC as "Sharing Publicly" — accurate for
   * Watch Later / Favorites / custom playlists where logged-out viewers can
   * see the row. 'activity' labels PUBLIC as "Members Only" — Currently
   * Watching / Watch History resolvers reject anonymous viewers in the
   * video-analytics subgraph, so even a "public" flag is members-only in
   * practice.
   */
  mode?: 'playlist' | 'activity'
}

interface PlaylistSharingDropdownOuterProps extends PlaylistSharingDropdownProps {
  /** Static-text Map injected by `connectStaticText({ storeKey: 'playlistSharingDropdown' })`. */
  staticText: InternationalStrings
}

function PlaylistSharingDropdownContent ({
  visibility,
  onChangeVisibility,
  allowedVisibilities,
  mode = 'playlist',
}: PlaylistSharingDropdownProps) {
  const {
    isOpen, ref: dropdownRef, close, toggle,
  } = useDisclosure()

  // Pull every label via the static-text hook — provider is established by
  // the Outer wrapper below.  Keys are namespaced under `sharing` following
  // the OracleCommons convention so the JSON shape mirrors
  // `OraclePage/lang_en.json`.
  const stSharingPublicly = useTranslatedText({ keys: ['sharing', 'sharingPublicly'] })
  const stSharingAnonymously = useTranslatedText({ keys: ['sharing', 'sharingAnonymously'] })
  const stNotSharing = useTranslatedText({ keys: ['sharing', 'notSharing'] })
  const stShareWithName = useTranslatedText({ keys: ['sharing', 'shareWithName'] })
  const stShareWithNameDesc = useTranslatedText({ keys: ['sharing', 'shareWithNameDesc'] })
  const stShareAnonymously = useTranslatedText({ keys: ['sharing', 'shareAnonymously'] })
  const stShareAnonymouslyDesc = useTranslatedText({ keys: ['sharing', 'shareAnonymouslyDesc'] })
  const stDontShare = useTranslatedText({ keys: ['sharing', 'dontShare'] })
  const stDontShareDesc = useTranslatedText({ keys: ['sharing', 'dontShareDesc'] })
  const stMembersOnly = useTranslatedText({ keys: ['sharing', 'membersOnly'] })
  const stShareWithMembers = useTranslatedText({ keys: ['sharing', 'shareWithMembers'] })
  const stShareWithMembersDesc = useTranslatedText({ keys: ['sharing', 'shareWithMembersDesc'] })
  const stProfileSharing = useTranslatedText({ keys: ['sharing', 'profileSharing'] })

  const isActivityMode = mode === 'activity'

  const badgeLabel = useMemo<Record<string, string>>(() => ({
    [VISIBILITY.PUBLIC]: isActivityMode ? stMembersOnly : stSharingPublicly,
    [VISIBILITY.ANONYMOUS]: stSharingAnonymously,
    [VISIBILITY.PRIVATE]: stNotSharing,
  }), [isActivityMode, stMembersOnly, stSharingPublicly, stSharingAnonymously, stNotSharing])

  const sharingIconSvg = visibility === VISIBILITY.PRIVATE
    ? <EyeHideSolid size="16px" />
    : <EyeShowSolid size="16px" />
  const sharingVariant = visibility === VISIBILITY.PRIVATE ? 'gray' as const : 'teal' as const
  const chevronIcon = isOpen
    ? <ChevronUp size="16px" />
    : <ChevronDown size="16px" />

  const allOptions = useMemo(() => [
    {
      value: VISIBILITY.PUBLIC,
      label: isActivityMode ? stShareWithMembers : stShareWithName,
      description: isActivityMode ? stShareWithMembersDesc : stShareWithNameDesc,
    },
    {
      value: VISIBILITY.ANONYMOUS,
      label: stShareAnonymously,
      description: stShareAnonymouslyDesc,
    },
    {
      value: VISIBILITY.PRIVATE,
      label: stDontShare,
      description: stDontShareDesc,
    },
  ], [
    isActivityMode,
    stShareWithName, stShareWithNameDesc,
    stShareWithMembers, stShareWithMembersDesc,
    stShareAnonymously, stShareAnonymouslyDesc,
    stDontShare, stDontShareDesc,
  ])

  const options = useMemo(() => (allowedVisibilities
    ? allOptions.filter((opt) => allowedVisibilities.includes(opt.value))
    : allOptions), [allOptions, allowedVisibilities])

  return (
    <div className="profile-my-playlists__sharing" ref={dropdownRef}>
      <CommunitySplitPillTag
        leftIconSvg={sharingIconSvg}
        leftLabel={badgeLabel[visibility]}
        leftTrailingIconSvg={chevronIcon}
        variant={sharingVariant}
        onClick={toggle}
        ariaHasPopup="listbox"
        ariaExpanded={isOpen}
      />
      {isOpen ? (
        <div className="profile-my-playlists__sharing-dropdown" role="listbox">
          <p className="profile-my-playlists__sharing-dropdown-title">
            {stProfileSharing}
          </p>
          {options.map((opt) => (
            <Button
              key={opt.value}
              type={BUTTON_TYPES.GHOST}
              role="option"
              aria-selected={visibility === opt.value}
              className={`profile-my-playlists__sharing-option${visibility === opt.value ? ' profile-my-playlists__sharing-option--selected' : ''}`}
              onClick={() => {
                onChangeVisibility(opt.value)
                close()
              }}
            >
              <span className="profile-my-playlists__sharing-option-radio">
                {visibility === opt.value ? (
                  <Check size={16} />
                ) : null}
              </span>
              <span className="profile-my-playlists__sharing-option-text">
                <span className="profile-my-playlists__sharing-option-label">
                  {opt.label}
                </span>
                <span className="profile-my-playlists__sharing-option-desc">
                  {opt.description}
                </span>
              </span>
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// Outer wrapper: receives `staticText` from connectStaticText and provides
// it on context so Content's `useTranslatedText` resolves against the
// `playlistSharingDropdown` slice instead of whatever provider happens to
// be nearest in the tree (e.g. `CommunityPlaylistRowHeader`'s).  Mirrors
// the Oracle Outer/Inner pattern.
//
// React.memo avoids re-rendering a row's dropdown when its parent re-renders
// but the dropdown's own props are unchanged (shallow-equal).  Paired with
// the row-level `useCallback` in ProfileMyPlaylists that stabilizes
// `onChangeVisibility`, this eliminates the sibling-cascade render pattern
// where toggling one row's visibility re-rendered every sibling dropdown.
function PlaylistSharingDropdown ({
  staticText,
  ...rest
}: PlaylistSharingDropdownOuterProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <PlaylistSharingDropdownContent {...rest} />
    </StaticTextProvider>
  )
}

export default compose(
  connectStaticText({ storeKey: 'playlistSharingDropdown' }),
)(React.memo(PlaylistSharingDropdown))
